import {
    MouseEvent, useCallback, useEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-rainbow-components';
import { isEmpty } from '@rainbow-modules/validation';
import { TableProps } from 'react-rainbow-components/components/Table';
import useCustomers from 'data/firestore/agent/customer/useCollectionWithPagination';
import { orderBy, OrderByDirection, query } from 'firebase/firestore';
import Pagination from 'components/Pagination';
import DateTimeColumn from 'components/DateTimeColumn';
import Table from 'components/Table';
import useSearchCustomers from 'data/hooks/useSearchCustomers';
import useCustomSearchParams from 'hooks/useCustomSearchParams';
import { useHasRequiredRoles } from '@voxityai/auth/hooks';
import { auth } from 'data/firestore/firebase';
import { SortOrder } from 'types';
import { EmptyLabel } from './columns/styled';
import {
    PAGE_SIZE,
} from '../../../constants';
import LanguageColumn from './columns/preferredLanguage';
import { CustomerColumn } from './columns/user';
import {
    Container, EntriesText, PaginationContainer, CellWrapper,
} from './styled';
import GenderColumn from './columns/gender';
import ActionsColumn from './columns/actions';

const dobColumnFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'utc',
};

const CustomersList = () => {
    const { agentId = '' } = useParams<{ agentId: string }>();
    const { hasAccess: isAdmin } = useHasRequiredRoles({ auth, allowedRoles: ['admin'] });

    const [searchParams, setSearchParams] = useCustomSearchParams({
        page: {
            type: 'number',
            default: 1,
        },
        sortDirection: {
            type: 'string',
            default: 'desc',
        },
        search: {
            type: 'string',
            default: '',
        },
        createdAt: {
            type: 'string',
            default: undefined as unknown as string,
        },
        tags: {
            type: 'string',
            default: undefined as unknown as string,
        },
        language: {
            type: 'string',
            default: undefined as unknown as string,
        },
    });

    const { page: activePage, sortDirection = 'desc' } = searchParams;
    const hasSearchFilters = Object.entries(searchParams)
        .filter(
            ([key, value]) => !['page', 'sortDirection'].includes(key) && !isEmpty(value),
        ).length > 0;

    const {
        data: customers,
        isLoading: isLoadingCustomers,
        totalRecords: totalCustomers,
        error,
    } = useCustomers(
        agentId,
        {
            disabled: !agentId || hasSearchFilters || activePage > 1,
            limit: PAGE_SIZE,
            listQueryFn: (ref) => query(ref, orderBy('createdAt', sortDirection as OrderByDirection)),
            track: [agentId, sortDirection],
        },
    );

    const searchEnabled = Boolean(hasSearchFilters || activePage > 1);

    const { data: searchCustomersData, isLoading: isLoadingSearchCustomers } = useSearchCustomers({
        agentId,
        searchQuery: searchParams.search,
        createdAt: searchParams.createdAt,
        preferredLanguage: searchParams.language,
        tags: searchParams.tags,
        sortBy: 'createdAt',
        sortOrder: sortDirection as SortOrder,
        pageSize: PAGE_SIZE,
        page: activePage,
        queryOptions: {
            enabled: searchEnabled,
        },
    });

    const dataToShow = (searchEnabled ? searchCustomersData?.hits : customers) || [];
    const totalHits = searchEnabled && typeof searchCustomersData?.pagination.total === 'number'
        ? searchCustomersData.pagination.total
        : totalCustomers;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNumber: number) => {
        if (isLoadingCustomers) return;
        setSearchParams({ page: pageNumber });
    };

    useEffect(() => {
        const totalPages = searchCustomersData?.pagination.total;
        if (isLoadingSearchCustomers) return;
        if (totalPages === 0) {
            setSearchParams({ page: undefined }, true);
        }
        if (
            totalPages !== undefined
                && totalPages > 0
                && activePage > totalPages
        ) {
            setSearchParams({ page: 1 }, true);
        }
    }, [
        searchCustomersData,
        isLoadingSearchCustomers,
        activePage,
        setSearchParams,
    ]);

    const handleOnSort = useCallback(
        (
            event: MouseEvent<HTMLElement>,
            field: string,
            nextSortDirection: 'asc' | 'desc',
        ) => {
            setSearchParams({ sortDirection: nextSortDirection });
        },
        [setSearchParams],
    );

    const isLoading = isLoadingCustomers || isLoadingSearchCustomers;

    if (error || (!customers && !isLoading)) {
        return (
            <Container>
                <h1>Something went wrong</h1>
                {error?.message && (
                    <p>
                        {error.name}
                        :
                        {' '}
                        {error.code}
                    </p>
                )}
            </Container>
        );
    }

    return (
        <Container>
            <Table
                data={dataToShow}
                keyField="id"
                isLoading={isLoading}
                sortedBy="createdAt"
                onSort={handleOnSort}
                sortDirection={sortDirection as TableProps['sortDirection']}
            >
                <Column
                    header="Customer"
                    field="id"
                    component={CustomerColumn}
                />
                <Column
                    header="Preferred Language"
                    field="preferredLanguage"
                    component={LanguageColumn}
                />
                <Column
                    header="DOB"
                    field="dob"
                    component={DateTimeColumn}
                    options={dobColumnFormatOptions}
                    emptyLabel={<CellWrapper><EmptyLabel>Not Provided</EmptyLabel></CellWrapper>}
                />
                <Column
                    header="Gender"
                    field="gender"
                    component={GenderColumn}
                />
                <Column
                    header="Created At"
                    field="createdAt"
                    component={DateTimeColumn}
                    sortable
                />
                {isAdmin && (
                    <Column
                        header="Actions"
                        component={ActionsColumn}
                        defaultWidth={100}
                    />
                )}
            </Table>
            <PaginationContainer>
                <EntriesText>
                    Showing
                    {' '}
                    {firstItemOfActivePage}
                    {' '}
                    to
                    {' '}
                    {lastItemOfActivePage}
                    {' '}
                    of
                    {' '}
                    {totalHits}
                    {' '}
                    entries.
                </EntriesText>
                <Pagination
                    activePage={activePage}
                    pages={pages}
                    onChange={handlePageChange}
                />
            </PaginationContainer>
        </Container>
    );
};

export default CustomersList;
